import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faPlus,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faTrash,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';

import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import {
  createCertificate,
  checkCertificatePurchaseStatus,
  deleteCertificatesByTask,
  fetchCertificateDetail,
  fetchCertificates,
  markTaskCertificatePurchased,
} from '../../redux/actions/certificateActions';

const SORTABLE_FIELDS = [
  'created_at',
  'updated_at',
  'certificate_number',
  'song_title',
  'task_id',
];

const toUnixSecondsFromDateTimeLocal = (value) => {
  if (!value) return undefined;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return undefined;
  return Math.floor(dt.getTime() / 1000);
};

const formatTimestamp = (value) => {
  if (!value) return 'N/A';

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return 'N/A';
    return value.toLocaleString();
  }

  if (typeof value === 'string') {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleString();
  }

  if (typeof value === 'number') {
    if (value === 0) return 'N/A';
    const ms = value > 9999999999 ? value : value * 1000;
    const dt = new Date(ms);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleString();
  }

  return 'N/A';
};

const renderSortIcon = (sortBy, sortOrder, field) => {
  if (sortBy !== field) return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted" />;
  return sortOrder === 'asc'
    ? <FontAwesomeIcon icon={faSortUp} className="ms-1" />
    : <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
};

const SongCertificatesAdmin = () => {
  const dispatch = useDispatch();

  const {
    items,
    total,
    loading,
    error,
    detail,
    detailLoading,
    detailError,
    createLoading,
    deleteLoading,
    purchaseStatusLoading,
    markPurchasedLoading,
  } = useSelector((state) => ({
    items: state.certificates.items || [],
    total: state.certificates.total || 0,
    loading: state.certificates.loading,
    error: state.certificates.error,
    detail: state.certificates.detail,
    detailLoading: state.certificates.detailLoading,
    detailError: state.certificates.detailError,
    createLoading: state.certificates.createLoading,
    deleteLoading: state.certificates.deleteLoading,
    purchaseStatusLoading: state.certificates.purchaseStatusLoading,
    markPurchasedLoading: state.certificates.markPurchasedLoading,
  }));

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 500);

  const [taskId, setTaskId] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [songTitle, setSongTitle] = useState('');
  const [creditTo, setCreditTo] = useState('');

  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailLink, setDetailLink] = useState('');

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [taskIdToDelete, setTaskIdToDelete] = useState('');

  const [showPurchaseStatusModal, setShowPurchaseStatusModal] = useState(false);
  const [purchaseStatusTaskId, setPurchaseStatusTaskId] = useState('');
  const [checkedPurchaseStatus, setCheckedPurchaseStatus] = useState(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const params = useMemo(() => {
    const p = {
      page: currentPage,
      items_per_page: Math.min(Math.max(parseInt(itemsPerPage, 10) || 20, 1), 1000),
      sort_by: SORTABLE_FIELDS.includes(sortBy) ? sortBy : 'created_at',
      sort_order: sortOrder === 'asc' ? 'asc' : 'desc',
    };

    if (debouncedQ) p.q = debouncedQ;
    if (taskId) p.task_id = taskId;
    if (certificateNumber) p.certificate_number = certificateNumber;
    if (songTitle) p.song_title = songTitle;
    if (creditTo) p.credit_to = creditTo;

    return p;
  }, [currentPage, itemsPerPage, sortBy, sortOrder, debouncedQ, taskId, certificateNumber, songTitle, creditTo]);

  useEffect(() => {
    dispatch(fetchCertificates(params));
  }, [dispatch, params]);

  const onSort = (field) => {
    if (!SORTABLE_FIELDS.includes(field)) return;
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const openDetail = (id, link) => {
    setDetailId(id ?? null);
    setDetailLink(link || '');
    setShowDetailModal(true);
    if (id != null) dispatch(fetchCertificateDetail(id));
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setDetailId(null);
    setDetailLink('');
  };

  const openDeleteByTask = (task_id) => {
    setTaskIdToDelete(task_id || '');
    setShowDeleteConfirmModal(true);
  };

  const closeDeleteByTask = () => {
    setShowDeleteConfirmModal(false);
    setTaskIdToDelete('');
  };

  const openPurchaseStatusModal = () => {
    setShowPurchaseStatusModal(true);
    setPurchaseStatusTaskId('');
    setCheckedPurchaseStatus(null);
  };

  const closePurchaseStatusModal = () => {
    setShowPurchaseStatusModal(false);
    setPurchaseStatusTaskId('');
    setCheckedPurchaseStatus(null);
  };

  const handleConfirmDeleteByTask = async () => {
    if (!taskIdToDelete) {
      showToastMessage('Task ID is required', 'error');
      return;
    }
    const result = await dispatch(deleteCertificatesByTask(taskIdToDelete));
    if (result?.ok) {
      showToastMessage(`Deleted ${result.data?.deleted || 0} certificate(s)`, 'success');
      closeDeleteByTask();
      dispatch(fetchCertificates({ ...params, page: 1 }));
      setCurrentPage(1);
    } else {
      showToastMessage(result?.message || 'Failed to delete certificates', 'error');
    }
  };

  const handleCheckPurchaseStatus = async () => {
    const value = purchaseStatusTaskId.trim();
    if (!value) {
      showToastMessage('Task ID is required', 'error');
      return;
    }
    const result = await dispatch(checkCertificatePurchaseStatus(value));
    if (result?.ok) {
      setCheckedPurchaseStatus(result.data || null);
    } else {
      setCheckedPurchaseStatus(null);
      showToastMessage(result?.message || 'Failed to check purchase status', 'error');
    }
  };

  const handleMarkPurchased = async () => {
    const task_id = checkedPurchaseStatus?.task_id || purchaseStatusTaskId.trim();
    if (!task_id) {
      showToastMessage('Task ID is required', 'error');
      return;
    }
    const result = await dispatch(markTaskCertificatePurchased({ task_id }));
    if (result?.ok) {
      setCheckedPurchaseStatus((prev) => (prev ? { ...prev, purchased: true } : prev));
      showToastMessage(result?.message || 'Certificate purchase recorded for task', 'success');
    } else {
      showToastMessage(result?.message || 'Failed to mark purchased', 'error');
    }
  };

  const [createForm, setCreateForm] = useState({
    task_id: '',
    song_title: '',
    credit_to: '',
    song_creation_platform: 'SongSnap',
    song_creation_region: 'US',
    device_id: '',
    song_creation_date_local: '',
    link: '',
    generate_file: false,
    force_new: false,
  });

  const updateCreateForm = (key, value) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetCreateForm = () => {
    setCreateForm({
      task_id: '',
      song_title: '',
      credit_to: '',
      song_creation_platform: 'SongSnap',
      song_creation_region: 'US',
      device_id: '',
      song_creation_date_local: '',
      link: '',
      generate_file: false,
      force_new: false,
    });
  };

  const handleCreate = async () => {
    if (!createForm.task_id || !createForm.song_title || !createForm.credit_to || !createForm.song_creation_platform || !createForm.song_creation_region) {
      showToastMessage('Please fill all required fields', 'error');
      return;
    }

    const payload = {
      task_id: createForm.task_id,
      song_title: createForm.song_title,
      credit_to: createForm.credit_to,
      song_creation_platform: createForm.song_creation_platform,
      song_creation_region: createForm.song_creation_region,
      generate_file: !!createForm.generate_file,
      force_new: !!createForm.force_new,
    };

    if (createForm.device_id) payload.device_id = createForm.device_id;
    if (createForm.link) payload.link = createForm.link;

    const ts = toUnixSecondsFromDateTimeLocal(createForm.song_creation_date_local);
    if (ts) payload.song_creation_date = ts;

    const result = await dispatch(createCertificate(payload));
    if (result?.ok) {
      showToastMessage('Certificate created', 'success');
      setShowCreateModal(false);
      resetCreateForm();
      dispatch(fetchCertificates({ ...params, page: 1 }));
      setCurrentPage(1);
    } else {
      showToastMessage(result?.message || 'Failed to create certificate', 'error');
    }
  };

  useEffect(() => {
    const link = detail?.link;
    if (showDetailModal && link) {
      setDetailLink(link);
    }
  }, [detail?.link, showDetailModal]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Song Certificates</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={openPurchaseStatusModal}>
            Check Purchase Status
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible>
          {typeof error === 'string' ? error : (error.message || 'Failed to load certificates')}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap gap-3 mb-4">
            <div className="flex-grow-1" style={{ minWidth: 260 }}>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search (q) across song title, credit to, certificate number, task id..."
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            <div style={{ minWidth: 220 }}>
              <Form.Control
                type="text"
                placeholder="Task ID filter"
                value={taskId}
                onChange={(e) => {
                  setTaskId(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div style={{ minWidth: 180 }}>
              <Form.Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
              >
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={200}>200 / page</option>
                <option value={500}>500 / page</option>
                <option value={1000}>1000 / page</option>
              </Form.Select>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-4">
            <div style={{ minWidth: 220 }}>
              <Form.Control
                type="text"
                placeholder="Certificate Number (LIKE)"
                value={certificateNumber}
                onChange={(e) => {
                  setCertificateNumber(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <Form.Control
                type="text"
                placeholder="Song Title (LIKE)"
                value={songTitle}
                onChange={(e) => {
                  setSongTitle(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <Form.Control
                type="text"
                placeholder="Credit To (LIKE)"
                value={creditTo}
                onChange={(e) => {
                  setCreditTo(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="table-responsive">
            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={(p) => setCurrentPage(p)}
              position="top"
            />

            <Table responsive hover>
              <thead>
                <tr>
                  <th
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('certificate_number')}
                  >
                    Certificate # {renderSortIcon(sortBy, sortOrder, 'certificate_number')}
                  </th>
                  <th
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('song_title')}
                  >
                    Song title {renderSortIcon(sortBy, sortOrder, 'song_title')}
                  </th>
                  <th>Credit to</th>
                  <th
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('task_id')}
                  >
                    Task ID {renderSortIcon(sortBy, sortOrder, 'task_id')}
                  </th>
                  <th
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('created_at')}
                  >
                    Created {renderSortIcon(sortBy, sortOrder, 'created_at')}
                  </th>
                  <th
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSort('updated_at')}
                  >
                    Updated {renderSortIcon(sortBy, sortOrder, 'updated_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      <div className="loading-spinner">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No certificates found
                    </td>
                  </tr>
                ) : (
                  items.map((c) => (
                    <tr key={c.id || `${c.task_id}-${c.certificate_number}`}>
                      <td>
                        {c.certificate_number ? (
                          <Badge bg="secondary" className="text-white">
                            {c.certificate_number}
                          </Badge>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td>{c.song_title || <span className="text-muted">N/A</span>}</td>
                      <td>{c.credit_to || <span className="text-muted">N/A</span>}</td>
                      <td>
                        <span className="text-break">{c.task_id || '—'}</span>
                      </td>
                      <td>{formatTimestamp(c.created_at)}</td>
                      <td>{formatTimestamp(c.updated_at)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteByTask(c.task_id)}
                            title="Delete by task id"
                            disabled={deleteLoading || !c.task_id}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => openDetail(c.id, c.link)}
                            disabled={!c.link && !c.id}
                            title={c.link || c.id ? 'View certificate' : 'No link'}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={(p) => setCurrentPage(p)}
              position="bottom"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Create Certificate (Admin)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Task ID *</Form.Label>
                <Form.Control
                  value={createForm.task_id}
                  onChange={(e) => updateCreateForm('task_id', e.target.value)}
                  placeholder="test-task-001"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Device ID</Form.Label>
                <Form.Control
                  value={createForm.device_id}
                  onChange={(e) => updateCreateForm('device_id', e.target.value)}
                  placeholder="Optional"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Song title *</Form.Label>
                <Form.Control
                  value={createForm.song_title}
                  onChange={(e) => updateCreateForm('song_title', e.target.value)}
                  placeholder="My Song"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Credit to *</Form.Label>
                <Form.Control
                  value={createForm.credit_to}
                  onChange={(e) => updateCreateForm('credit_to', e.target.value)}
                  placeholder="John Doe"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Song creation platform *</Form.Label>
                <Form.Control
                  value={createForm.song_creation_platform}
                  onChange={(e) => updateCreateForm('song_creation_platform', e.target.value)}
                  placeholder="SongSnap"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Song creation region *</Form.Label>
                <Form.Control
                  value={createForm.song_creation_region}
                  onChange={(e) => updateCreateForm('song_creation_region', e.target.value)}
                  placeholder="US"
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Song creation date (optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={createForm.song_creation_date_local}
                  onChange={(e) => updateCreateForm('song_creation_date_local', e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Link (optional)</Form.Label>
                <Form.Control
                  value={createForm.link}
                  onChange={(e) => updateCreateForm('link', e.target.value)}
                  placeholder="https://..."
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Check
                type="checkbox"
                id="create-generate-file"
                label="Generate file (backend uploads PDF/PNG if link empty)"
                checked={createForm.generate_file}
                onChange={(e) => updateCreateForm('generate_file', e.target.checked)}
              />
            </div>
            <div className="col-12">
              <Form.Check
                type="checkbox"
                id="create-force-new"
                label="Force new (if false, backend returns existing by task_id)"
                checked={createForm.force_new}
                onChange={(e) => updateCreateForm('force_new', e.target.checked)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={createLoading}>
            {createLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faCheck} className="me-2" />Create</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={closeDetail} size="lg" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Certificate {detailId != null ? `#${detailId}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading && !detailLink ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : detailError && !detailLink ? (
            <Alert variant="danger">{detailError}</Alert>
          ) : detailLink ? (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-muted small text-truncate" style={{ maxWidth: '70%' }}>
                  {detailLink}
                </div>
                <a
                  href={detailLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small"
                >
                  Open in new tab
                </a>
              </div>
              {detailLink.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={detailLink}
                  title="Certificate PDF"
                  style={{ width: '100%', height: '70vh', border: '1px solid #e5e7eb', borderRadius: 8 }}
                />
              ) : detailLink.match(/\.(png|jpg|jpeg|webp)$/i) ? (
                <div className="text-center">
                  <img
                    src={detailLink}
                    alt="Certificate"
                    style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }}
                  />
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  Preview is not available for this link type. Use “Open in new tab”.
                </Alert>
              )}
            </div>
          ) : (
            <div className="text-muted">No link available</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Delete by Task ID Confirm Modal */}
      <Modal show={showDeleteConfirmModal} onHide={closeDeleteByTask} centered scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>Delete certificates by Task ID</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>All certificates for the given Task ID will be deleted. This cannot be undone.</p>
          <Form.Control
            type="text"
            placeholder="Task ID"
            value={taskIdToDelete}
            onChange={(e) => setTaskIdToDelete(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteByTask}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDeleteByTask} disabled={deleteLoading || !taskIdToDelete}>
            {deleteLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faTrash} className="me-2" />Delete</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Check Purchase Status Modal */}
      <Modal show={showPurchaseStatusModal} onHide={closePurchaseStatusModal} centered scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>Check Certificate Purchase Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Task ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter task id"
              value={purchaseStatusTaskId}
              onChange={(e) => setPurchaseStatusTaskId(e.target.value)}
            />
          </Form.Group>

          {checkedPurchaseStatus && (
            <Card className="border-0 bg-light">
              <Card.Body className="py-3">
                <div className="mb-2"><strong>Task ID:</strong> <span className="text-break">{checkedPurchaseStatus.task_id}</span></div>
                <div className="mb-2">
                  <strong>Purchased:</strong>{' '}
                  <Badge bg={checkedPurchaseStatus.purchased ? 'success' : 'warning'} className={checkedPurchaseStatus.purchased ? 'text-white' : 'text-dark'}>
                    {checkedPurchaseStatus.purchased ? 'true' : 'false'}
                  </Badge>
                </div>
                {typeof checkedPurchaseStatus.uses_credit_system === 'boolean' && (
                  <div className="mb-2">
                    <strong>Uses credit system:</strong>{' '}
                    <Badge bg={checkedPurchaseStatus.uses_credit_system ? 'info' : 'secondary'} className={checkedPurchaseStatus.uses_credit_system ? 'text-dark' : 'text-white'}>
                      {checkedPurchaseStatus.uses_credit_system ? 'true' : 'false'}
                    </Badge>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={closePurchaseStatusModal}>
            Close
          </Button>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handleCheckPurchaseStatus}
              disabled={purchaseStatusLoading || !purchaseStatusTaskId.trim()}
            >
              {purchaseStatusLoading ? <Spinner animation="border" size="sm" /> : 'Check status'}
            </Button>
            {checkedPurchaseStatus?.purchased === false && checkedPurchaseStatus?.uses_credit_system === true && (
              <Button
                variant="success"
                onClick={handleMarkPurchased}
                disabled={markPurchasedLoading}
              >
                {markPurchasedLoading ? <Spinner animation="border" size="sm" /> : 'Mark purchased'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={5000} autohide bg={toastType === 'success' ? 'success' : 'danger'}>
          <Toast.Header closeButton>
            <strong className="me-auto">{toastType === 'success' ? 'Success' : 'Error'}</strong>
          </Toast.Header>
          <Toast.Body className={toastType === 'error' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default SongCertificatesAdmin;
